import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getWithExpiry } from '../../../Domain/Helpers/LocalStorageHelper';
import { getAggregators } from '../../../Infrastructure/Services/AggregatorService';
import { getProviders, getDbProviders, getSimilarProviderNames } from '../../../Infrastructure/Services/ProviderService';
import ProviderPairSelector from '../../../Domain/Components/UI/ProviderPairSelector/ProviderPairSelector';
import { getCasinos } from '../../../Infrastructure/Services/CasinoService';
import {
  getGameList,
  importOperatorGameConfigs,
  importGameConfigs,
} from '../../../Infrastructure/Services/GameService';
import { importMissingProviders } from '../../../Infrastructure/Services/ProviderService';

const getGameCode = (g) => g.provider_game_id ?? g.game_code ?? g.code ?? String(g.id);

const stripCasinoPrefix = (internalId) => {
  if (!internalId) return null;
  const idx = internalId.indexOf('-flx-');
  return idx !== -1 ? internalId.slice(idx + 1) : internalId;
};

/* Renders a badge for is_active_game_config: 1 = Active, 0 = Inactive, null = Not in DB */
function activeConfigBadge(val) {
  if (val === 1)    return <span className="badge bg-success">Active</span>;
  if (val === 0)    return <span className="badge bg-danger">Inactive</span>;
  return <span className="badge bg-secondary">—</span>;
}

/* ─── Import Result Modal ─── */
function ImportResultModal({ result, onClose }) {
  if (!result) return null;
  const { created = 0, activated = 0, skipped = 0, skipped_details = [] } = result;
  const total = created + activated + skipped;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 10, padding: 28,
          minWidth: 420, maxWidth: 600, width: '90%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 style={{ margin: 0, fontWeight: 700, color: '#2e2e31' }}>Import Result</h5>
          <button
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888', lineHeight: 1 }}
            onClick={onClose}
          >×</button>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          {[
            ['#d1fae5','#065f46', created,   'Created'],
            ['#dbeafe','#1e3a8a', activated, 'Activated'],
            ['#fef3c7','#92400e', skipped,   'Skipped'],
            ['#f3f4f6','#374151', total,     'Total'],
          ].map(([bg, color, val, label]) => (
            <div key={label} style={{ flex: 1, background: bg, color, borderRadius: 8, padding: '12px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 800 }}>{val}</div>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
            </div>
          ))}
        </div>

        {skipped_details.length > 0 && (
          <>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Skipped Details
            </p>
            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #fde68a', borderRadius: 6, background: '#fffbeb', padding: '6px 0' }}>
              {skipped_details.map((msg, i) => {
                const colonIdx = msg.indexOf(':');
                const code   = colonIdx > -1 ? msg.substring(0, colonIdx) : null;
                const reason = colonIdx > -1 ? msg.substring(colonIdx + 1).trim() : msg;
                return (
                  <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 12px', borderBottom: i < skipped_details.length - 1 ? '1px solid #fde68a' : 'none', alignItems: 'flex-start' }}>
                    <code style={{ fontSize: 11, color: '#92400e', flexShrink: 0, marginTop: 1 }}>{code}</code>
                    <span style={{ fontSize: 12, color: '#78350f' }}>{reason}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            {result.csv_url && (
              <a href={result.csv_url} download className="btn btn-outline-secondary btn-sm">
                Download CSV
              </a>
            )}
          </div>
          <button className="btn btn-fadeblack btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Confirm Modal ─── */
function ConfirmModal({ title, children, confirmLabel = 'Confirm', onConfirm, onCancel, confirming }) {
  return (
    <div style={{ position:'fixed', inset:0, zIndex:2500, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onCancel}>
      <div style={{ background:'#fff', borderRadius:10, padding:24, minWidth:380, maxWidth:520,
        width:'90%', boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
        <h5 style={{ margin:'0 0 12px', fontWeight:700, color:'#2e2e31' }}>{title}</h5>
        <div style={{ fontSize:13, color:'#374151', marginBottom:20 }}>{children}</div>
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={confirming}>Cancel</button>
          <button className="btn btn-flexpink btn-sm" onClick={onConfirm} disabled={confirming}>
            {confirming ? <><span className="spinner-border spinner-border-sm me-1" />Working…</> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── shared primitives ─── */
function StepLabel({ n, text }) {
  return (
    <div className="d-flex align-items-center mb-2">
      <span className="step-badge">{n}</span>
      <span style={{ fontWeight: 700, color: '#2e2e31' }}>{text}</span>
    </div>
  );
}

function SelectInput({ label, value, onChange, disabled, children }) {
  return (
    <div className="filter-group">
      {label && <label className="filter-label">{label}</label>}
      <select
        className="form-select form-select-sm"
        style={{ minWidth: 180 }}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
      >
        {children}
      </select>
    </div>
  );
}

/* ─── Searchable dropdown ─── */
function SearchableSelect({ value, onChange, disabled, options, placeholder = '— Select —', labelKey = 'label', valueKey = 'value' }) {
  const [search, setSearch] = React.useState('');
  const [open, setOpen]     = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Reset search when closed
  React.useEffect(() => { if (!open) setSearch(''); }, [open]);

  const filtered = options.filter(o =>
    String(o[labelKey]).toLowerCase().includes(search.toLowerCase())
  );

  const selectedLabel = options.find(o => String(o[valueKey]) === String(value))?.[labelKey];

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 180 }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        style={{
          width: '100%', padding: '5px 30px 5px 10px', fontSize: 13,
          border: '1px solid #dee2e6', borderRadius: 4,
          background: disabled ? '#e9ecef' : '#fff',
          color: value ? '#2e2e31' : '#6c757d',
          cursor: disabled ? 'default' : 'pointer',
          textAlign: 'left', position: 'relative',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLabel ?? placeholder}
        </span>
        <span style={{ fontSize: 10, color: '#6c757d', flexShrink: 0, marginLeft: 6 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: '1px solid #dee2e6', borderRadius: 4,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: 2,
          minWidth: 220,
        }}>
          {/* Search input */}
          <div style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
            <input
              autoFocus
              type="text"
              className="form-control form-control-sm"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
              style={{ fontSize: 12 }}
            />
          </div>
          {/* Options */}
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <div
              onClick={() => { onChange(''); setOpen(false); }}
              style={{
                padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                color: '#6c757d',
                background: !value ? '#f8f9fa' : 'transparent',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
              onMouseLeave={e => e.currentTarget.style.background = !value ? '#f8f9fa' : 'transparent'}
            >
              {placeholder}
            </div>
            {filtered.length === 0 && (
              <div style={{ padding: '8px 12px', fontSize: 12, color: '#9ca3af' }}>No results</div>
            )}
            {filtered.map(o => (
              <div
                key={o[valueKey]}
                onClick={() => { onChange(String(o[valueKey])); setOpen(false); }}
                style={{
                  padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                  background: String(o[valueKey]) === String(value) ? '#fdf2f8' : 'transparent',
                  color: String(o[valueKey]) === String(value) ? '#e20075' : '#2e2e31',
                  fontWeight: String(o[valueKey]) === String(value) ? 600 : 400,
                }}
                onMouseEnter={e => { if (String(o[valueKey]) !== String(value)) e.currentTarget.style.background = '#f8f9fa'; }}
                onMouseLeave={e => { e.currentTarget.style.background = String(o[valueKey]) === String(value) ? '#fdf2f8' : 'transparent'; }}
              >
                {o[labelKey]}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   TAB 1 — Browse Games
   ══════════════════════════════════════════ */
function BrowseTab({ aggregators, providers, loadingProviders, aggId, onAggChange, casinos, setLoggedIn }) {
  const [providerId, setProviderId] = useState('');
  const [apiProviderName, setApiProviderName] = useState('');
  const [selectedCasinoIds, setSelectedCasinoIds] = useState([]);
  const [games, setGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);

  useEffect(() => {
    setProviderId('');
    setApiProviderName('');
    setGames([]);
  }, [providers]);

  const handlePairChange = (newProviderId, newApiProviderName) => {
    setProviderId(newProviderId);
    setApiProviderName(newApiProviderName);
    setGames([]);
  };

  const handleSearch = async () => {
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    if (!aggId || !providerId || !apiProviderName) { toast.warn('Select aggregator and provider'); return; }
    setLoadingGames(true); setGames([]);
    try {
      const casinoIds = selectedCasinoIds.length ? selectedCasinoIds.map(Number) : [];
      const result = await getGameList(token, Number(aggId), Number(providerId), apiProviderName, casinoIds);
      setGames(Array.isArray(result) ? result : []);
      if (!result?.length) toast.info('No games found for this selection');
    } catch { toast.error('Failed to load games'); }
    finally { setLoadingGames(false); }
  };

  const toggleCasino = (id) =>
    setSelectedCasinoIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div>
      <div className="card-section">
        <StepLabel n="1" text="Select Aggregator & Provider" />
        <SelectInput label="Aggregator" value={aggId} onChange={v => { onAggChange(v); setGames([]); }}>
          <option value="">— Select —</option>
          {aggregators.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </SelectInput>
        <ProviderPairSelector
          aggId={aggId}
          dbProviders={providers}
          loadingDbProviders={loadingProviders}
          providerId={providerId}
          apiProviderName={apiProviderName}
          onChange={handlePairChange}
          token={getWithExpiry('token')}
          setLoggedIn={setLoggedIn}
        />
      </div>

      <div className="card-section">
        <StepLabel n="2" text="Filter by Casino (optional) & Search" />
        <p style={{ fontSize: 12, color: '#607d8b', marginBottom: 10 }}>
          Leave all unchecked to search across all casinos.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          {casinos.map(c => {
            const checked = selectedCasinoIds.includes(c.id);
            return (
              <label key={c.id} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
                background: checked ? '#fce4ec' : '#f5f5f5',
                border: `1px solid ${checked ? '#e20075' : '#ddd'}`,
                fontSize: 13, fontWeight: checked ? 700 : 400,
                color: checked ? '#e20075' : '#555', transition: 'all 0.15s',
              }}>
                <input type="checkbox" checked={checked} onChange={() => toggleCasino(c.id)} style={{ display: 'none' }} />
                {c.company_name}
              </label>
            );
          })}
        </div>
        <button className="btn btn-fadeblack btn-sm" onClick={handleSearch} disabled={loadingGames || !aggId || !providerId}>
          {loadingGames ? <><span className="spinner-border spinner-border-sm me-1" /> Searching…</> : 'Search Games'}
        </button>
      </div>

      {(games.length > 0 || loadingGames) && (
        <div className="card-section">
          {loadingGames
            ? <div className="text-center p-3"><div className="spinner-border text-secondary" role="status" /></div>
            : <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0" style={{ color: '#2e2e31' }}>{games.length} games found</h6>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="table table-hover table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Provider</th>
                        <th>Game Name</th>
                        <th>Provider Game ID</th>
                        <th>BW Game Code</th>
                        <th>Category</th>
                        <th>In DB</th>
                        <th>Casino</th>
                      </tr>
                    </thead>
                    <tbody>
                      {games.map(g => {
                        const code = getGameCode(g);
                        return (
                          <tr key={code}>
                            <td style={{ fontSize: 12, color: '#607d8b' }}>{g.provider ?? '—'}</td>
                            <td><strong style={{ fontSize: 13 }}>{g.name}</strong></td>
                            <td><code style={{ fontSize: 11 }}>{code}</code></td>
                            <td>
                              {(() => {
                                const firstWithId = g.casinos?.find(c => c.internal_game_id);
                                const bwCode = stripCasinoPrefix(firstWithId?.internal_game_id);
                                return bwCode
                                  ? <code style={{ fontSize: 11 }}>{bwCode}</code>
                                  : <span className="text-muted">—</span>;
                              })()}
                            </td>
                            <td><span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>{g.category ?? '—'}</span></td>
                            <td>
                              <span className={`badge ${g.is_in_db ? 'bg-success' : 'bg-warning text-dark'}`}>
                                {g.is_in_db ? 'In DB' : 'Not in DB'}
                              </span>
                            </td>
                            <td>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {g.casinos?.length
                                  ? g.casinos.map(c => (
                                      <span key={c.casino_id}
                                        title={`Active: ${c.is_active ?? 'n/a'} | Internal ID: ${c.internal_game_id ?? 'n/a'}`}
                                        style={{
                                          display: 'inline-flex', alignItems: 'center', gap: 4,
                                          padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                                          background: c.is_added ? '#d1fae5' : '#f3f4f6',
                                          color: c.is_added ? '#065f46' : '#6b7280',
                                          border: `1px solid ${c.is_added ? '#6ee7b7' : '#e5e7eb'}`,
                                        }}>
                                        <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                                          background: c.is_added ? (c.is_active ? '#10b981' : '#f59e0b') : '#d1d5db' }} />
                                        {c.casino_name}
                                      </span>
                                    ))
                                  : <span className="text-muted" style={{ fontSize: 12 }}>—</span>
                                }
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 11, color: '#6b7280' }}>
                  {[['#10b981','Added & Active'],['#f59e0b','Added, Inactive'],['#d1d5db','Not Added']].map(([color, label]) => (
                    <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
                      {label}
                    </span>
                  ))}
                </div>
              </>
          }
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   TAB 2 — Import Game Configs (global)
   ══════════════════════════════════════════ */
function ImportGameConfigsTab({ aggregators, providers, loadingProviders, aggId, onAggChange, setLoggedIn }) {
  const [providerId, setProviderId] = useState('');
  const [apiProviderName, setApiProviderName] = useState('');
  const [games, setGames] = useState([]);
  const [selectedGames, setSelectedGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [similarWarnings, setSimilarWarnings] = useState([]);
  const [checkingSimilar, setCheckingSimilar] = useState(false);

  useEffect(() => {
    setProviderId('');
    setApiProviderName('');
    setGames([]); setSelectedGames([]);
  }, [providers]);

  const handlePairChange = (newProviderId, newApiProviderName) => {
    setProviderId(newProviderId);
    setApiProviderName(newApiProviderName);
    setGames([]); setSelectedGames([]);
  };

  const loadGames = useCallback(async () => {
    const token = getWithExpiry('token');
    if (!token || !aggId || !providerId) return;
    setLoadingGames(true); setGames([]); setSelectedGames([]);
    try {
      const result = await getGameList(token, Number(aggId), Number(providerId), apiProviderName, []);
      setGames(Array.isArray(result) ? result : []);
      if (!result?.length) toast.info('No games found');
    } catch { toast.error('Failed to load games'); }
    finally { setLoadingGames(false); }
  }, [aggId, providerId, apiProviderName]);

  const handleLoadGames = async () => {
    if (!aggId || !providerId) { toast.warn('Select aggregator and provider'); return; }
    await loadGames();
  };

  const canSelectConfig = (g) => !g.is_in_db || g.is_active_game_config === null || g.is_active_game_config === 0;

  const toggleGame = (code, selectable) => {
    if (!selectable) return;
    setSelectedGames(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };
  const selectableGames = games.filter(canSelectConfig);
  const allSelected = selectableGames.length > 0 && selectedGames.length === selectableGames.length;
  const toggleAll = () => setSelectedGames(allSelected ? [] : selectableGames.map(getGameCode));

  const openImportConfirm = async () => {
    if (!selectedGames.length) { toast.warn('Select at least one game'); return; }
    setShowImportConfirm(true);
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setCheckingSimilar(true);
    try {
      const result = await getSimilarProviderNames(token, aggId, providerId, apiProviderName, providers.find(p => String(p.id) === String(providerId))?.name);
      setSimilarWarnings(result ?? []);
    } finally { setCheckingSimilar(false); }
  };

  const handleImport = async () => {
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setImporting(true);
    try {
      const response = await importGameConfigs(token, {
        aggregatorId: Number(aggId),
        providerId: Number(providerId),
        apiProviderName,
        gameCodes: selectedGames,
      });
      setImportResult(response);
      setSelectedGames([]);
      setShowImportConfirm(false);
      await loadGames();
    } catch { toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  return (
    <div>
      {importResult && <ImportResultModal result={importResult} onClose={() => setImportResult(null)} />}

      {showImportConfirm && (
        <ConfirmModal
          title="Confirm Game Config Import"
          confirmLabel="Import"
          confirming={importing}
          onCancel={() => setShowImportConfirm(false)}
          onConfirm={handleImport}
        >
          <p>
            Import {selectedGames.length} game config{selectedGames.length !== 1 ? 's' : ''} using{' '}
            <strong>{apiProviderName}</strong> → <strong>{providers.find(p => String(p.id) === String(providerId))?.name ?? providerId}</strong>?
          </p>
          {checkingSimilar && (
            <div className="d-flex align-items-center gap-2" style={{ fontSize: 12, color: '#6b7280' }}>
              <span className="spinner-border spinner-border-sm" />
              Checking for similar providers…
            </div>
          )}
          {!checkingSimilar && similarWarnings.length > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginTop: 8 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#92400e', margin: '0 0 6px', textTransform: 'uppercase' }}>
                Similar DB Provider Names
              </p>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#78350f' }}>
                {similarWarnings.map(m => (
                  <li key={m.id}>
                    <strong>{m.name}</strong>
                    {m.exactApiNameMatch ? ' — exact match to API name' : ` (${m.score}%)`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </ConfirmModal>
      )}

      <div className="card-section">
        <StepLabel n="1" text="Select Aggregator & Provider" />
        <SelectInput label="Aggregator" value={aggId} onChange={v => { onAggChange(v); setGames([]); setSelectedGames([]); }}>
          <option value="">— Select —</option>
          {aggregators.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </SelectInput>
        <ProviderPairSelector
          aggId={aggId}
          dbProviders={providers}
          loadingDbProviders={loadingProviders}
          providerId={providerId}
          apiProviderName={apiProviderName}
          onChange={handlePairChange}
          token={getWithExpiry('token')}
          setLoggedIn={setLoggedIn}
        />
        <div className="filter-row">
          <div className="filter-group">
            <label className="filter-label">&nbsp;</label>
            <button className="btn btn-fadeblack btn-sm" onClick={handleLoadGames} disabled={loadingGames || !aggId || !providerId}>
              {loadingGames ? <><span className="spinner-border spinner-border-sm me-1" />Loading…</> : 'Load Games'}
            </button>
          </div>
        </div>
      </div>

      {(games.length > 0 || loadingGames) && (
        <div className="card-section">
          <StepLabel n="2" text="Select Games to Import" />
          <p style={{ fontSize: 12, color: '#607d8b', marginBottom: 12 }}>
            Games marked <span className="badge bg-success" style={{ fontSize: 10 }}>Active</span> are already active and cannot be re-imported.
            Games marked <span className="badge bg-warning text-dark" style={{ fontSize: 10 }}>Not in DB</span> will be created fresh.
          </p>
          {loadingGames
            ? <div className="text-center p-3"><div className="spinner-border text-secondary" role="status" /></div>
            : <div style={{ overflowX: 'auto' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    {selectedGames.length} / {selectableGames.length} selectable selected
                    {games.length !== selectableGames.length && (
                      <span style={{ color: '#6b7280', marginLeft: 8 }}>
                        ({games.length - selectableGames.length} already active)
                      </span>
                    )}
                  </span>
                  <button className="btn btn-outline-secondary btn-sm" onClick={toggleAll} disabled={selectableGames.length === 0}>
                    {allSelected ? 'Deselect All' : `Select All (${selectableGames.length})`}
                  </button>
                </div>
                <table className="table table-hover table-sm align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={selectableGames.length === 0} /></th>
                      <th>Name</th><th>Provider Game ID</th><th>Category</th><th>In DB</th><th>Config Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {games.map(g => {
                      const code = getGameCode(g);
                      const sel = selectedGames.includes(code);
                      const selectable = canSelectConfig(g);
                      return (
                        <tr key={code}
                          onClick={() => toggleGame(code, selectable)}
                          style={{ cursor: selectable ? 'pointer' : 'not-allowed', background: sel ? '#fdf2f8' : selectable ? undefined : '#fafafa', opacity: selectable ? 1 : 0.55 }}
                        >
                          <td onClick={e => e.stopPropagation()}><input type="checkbox" checked={sel} onChange={() => toggleGame(code, selectable)} disabled={!selectable} /></td>
                          <td>
                            <strong style={{ fontSize: 13 }}>{g.name}</strong>
                            {!selectable && <span style={{ fontSize: 10, color: '#6b7280', marginLeft: 6 }}>already active</span>}
                          </td>
                          <td><code style={{ fontSize: 11 }}>{code}</code></td>
                          <td><span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>{g.category ?? '—'}</span></td>
                          <td><span className={`badge ${g.is_in_db ? 'bg-success' : 'bg-warning text-dark'}`}>{g.is_in_db ? 'In DB' : 'Not in DB'}</span></td>
                          <td>{activeConfigBadge(g.is_active_game_config)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
          }
        </div>
      )}

      {selectedGames.length > 0 && (
        <div className="card-section">
          <StepLabel n="3" text="Confirm & Import" />
          <div className="import-panel">
            <h6>Global Game Config Import</h6>
            <table className="table table-sm mb-2" style={{ fontSize: 12 }}>
              <tbody>
                <tr><td className="text-muted" style={{ width: 130 }}>Aggregator</td><td><strong>{aggregators.find(a => String(a.id) === String(aggId))?.name ?? aggId}</strong></td></tr>
                <tr><td className="text-muted">Provider</td><td><strong>{providers.find(p => String(p.id) === String(providerId))?.name ?? providerId}</strong></td></tr>
                <tr><td className="text-muted">Games selected</td><td><strong>{selectedGames.length}</strong></td></tr>
                <tr><td className="text-muted">Endpoint</td><td><code style={{ fontSize: 11 }}>/import-game-configs</code></td></tr>
              </tbody>
            </table>
            <button className="btn btn-flexpink btn-sm" onClick={openImportConfirm} disabled={importing}>
              {`Import ${selectedGames.length} Game Config${selectedGames.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   TAB 3 — Import Operator Game Configs
   ══════════════════════════════════════════ */
function ImportOperatorTab({ aggregators, providers, loadingProviders, aggId, onAggChange, casinos, setLoggedIn }) {
  const [providerId, setProviderId] = useState('');
  const [apiProviderName, setApiProviderName] = useState('');
  const [casinoId, setCasinoId]     = useState(casinos[0] ? String(casinos[0].id) : '');
  const [games, setGames]           = useState([]);
  const [selectedGames, setSelectedGames] = useState([]);
  const [loadingGames, setLoadingGames]   = useState(false);
  const [importing, setImporting]         = useState(false);
  const [importResult, setImportResult]   = useState(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [similarWarnings, setSimilarWarnings] = useState([]);
  const [checkingSimilar, setCheckingSimilar] = useState(false);

  useEffect(() => {
    setProviderId('');
    setApiProviderName('');
    setGames([]); setSelectedGames([]);
  }, [providers]);

  useEffect(() => {
    if (casinos.length && !casinoId) setCasinoId(String(casinos[0].id));
  }, [casinos]);

  const handlePairChange = (newProviderId, newApiProviderName) => {
    setProviderId(newProviderId);
    setApiProviderName(newApiProviderName);
    setGames([]); setSelectedGames([]);
  };

  const loadGames = useCallback(async () => {
    const token = getWithExpiry('token');
    if (!token || !aggId || !providerId) return;
    setLoadingGames(true); setGames([]); setSelectedGames([]);
    try {
      const casinoFilter = casinoId ? [Number(casinoId)] : [];
      const result = await getGameList(token, Number(aggId), Number(providerId), apiProviderName, casinoFilter);
      setGames(Array.isArray(result) ? result : []);
      if (!result?.length) toast.info('No games found');
    } catch { toast.error('Failed to load games'); }
    finally { setLoadingGames(false); }
  }, [aggId, providerId, apiProviderName, casinoId]);

  const handleLoadGames = async () => {
    if (!aggId || !providerId) { toast.warn('Select aggregator and provider'); return; }
    await loadGames();
  };

  const selectableGames = games.filter(g => {
    const ce = g.casinos?.find(c => String(c.casino_id) === String(casinoId));
    const globalConfigReady = g.is_in_db && g.is_active_game_config === 1;
    const alreadyActive     = ce?.is_active === 1 || ce?.is_active === true;
    const alreadyAdded      = ce?.is_added === true || ce?.is_added === 1;
    return globalConfigReady && !(alreadyAdded && alreadyActive);
  });
  const allSelected = selectableGames.length > 0 && selectedGames.length === selectableGames.length;
  const toggleAll = () => setSelectedGames(allSelected ? [] : selectableGames.map(getGameCode));

  const toggleGame = (code, canSelect) => {
    if (!canSelect) return;
    setSelectedGames(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const openImportConfirm = async () => {
    if (!selectedGames.length) { toast.warn('Select at least one game'); return; }
    if (!casinoId) { toast.warn('Select a casino'); return; }
    setShowImportConfirm(true);
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setCheckingSimilar(true);
    try {
      const result = await getSimilarProviderNames(token, aggId, providerId, apiProviderName, providers.find(p => String(p.id) === String(providerId))?.name);
      setSimilarWarnings(result ?? []);
    } finally { setCheckingSimilar(false); }
  };

  const handleImport = async () => {
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setImporting(true);
    try {
      const response = await importOperatorGameConfigs(token, {
        aggregatorId: Number(aggId),
        providerId: Number(providerId),
        apiProviderName,
        casinoId: Number(casinoId),
        gameCodes: selectedGames,
      });
      if (response?.csv_url) {
        const link = document.createElement('a');
        link.href = response.csv_url;
        link.setAttribute('download', '');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setImportResult(response);
      setSelectedGames([]);
      setShowImportConfirm(false);
      await loadGames();
    } catch { toast.error('Operator import failed'); }
    finally { setImporting(false); }
  };

  const enrichedGames = games.map(g => ({
    ...g,
    _casinoEntry: g.casinos?.find(c => String(c.casino_id) === String(casinoId)) ?? null,
  }));

  return (
    <div>
      {importResult && <ImportResultModal result={importResult} onClose={() => setImportResult(null)} />}

      {showImportConfirm && (
        <ConfirmModal
          title="Confirm Operator Game Config Import"
          confirmLabel="Import"
          confirming={importing}
          onCancel={() => setShowImportConfirm(false)}
          onConfirm={handleImport}
        >
          <p>
            Import {selectedGames.length} operator game config{selectedGames.length !== 1 ? 's' : ''} using{' '}
            <strong>{apiProviderName}</strong> → <strong>{providers.find(p => String(p.id) === String(providerId))?.name ?? providerId}</strong>{' '}
            for <strong>{casinos.find(c => String(c.id) === String(casinoId))?.company_name ?? 'this casino'}</strong>?
          </p>
          {checkingSimilar && (
            <div className="d-flex align-items-center gap-2" style={{ fontSize: 12, color: '#6b7280' }}>
              <span className="spinner-border spinner-border-sm" />
              Checking for similar providers…
            </div>
          )}
          {!checkingSimilar && similarWarnings.length > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginTop: 8 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#92400e', margin: '0 0 6px', textTransform: 'uppercase' }}>
                Similar DB Provider Names
              </p>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#78350f' }}>
                {similarWarnings.map(m => (
                  <li key={m.id}>
                    <strong>{m.name}</strong>
                    {m.exactApiNameMatch ? ' — exact match to API name' : ` (${m.score}%)`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </ConfirmModal>
      )}

      <div className="card-section">
        <StepLabel n="1" text="Select Aggregator, Provider & Casino" />
        <SelectInput label="Aggregator" value={aggId} onChange={v => { onAggChange(v); setGames([]); setSelectedGames([]); }}>
          <option value="">— Select —</option>
          {aggregators.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </SelectInput>
        <ProviderPairSelector
          aggId={aggId}
          dbProviders={providers}
          loadingDbProviders={loadingProviders}
          providerId={providerId}
          apiProviderName={apiProviderName}
          onChange={handlePairChange}
          token={getWithExpiry('token')}
          setLoggedIn={setLoggedIn}
        />
        <div className="filter-row">
          <SelectInput label="Casino" value={casinoId} onChange={v => { setCasinoId(v); setGames([]); setSelectedGames([]); }}>
            <option value="">— Select —</option>
            {casinos.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </SelectInput>
          <div className="filter-group">
            <label className="filter-label">&nbsp;</label>
            <button className="btn btn-fadeblack btn-sm" onClick={handleLoadGames} disabled={loadingGames || !aggId || !providerId || !casinoId}>
              {loadingGames ? <><span className="spinner-border spinner-border-sm me-1" />Loading…</> : 'Load Games'}
            </button>
          </div>
        </div>
      </div>

      {(games.length > 0 || loadingGames) && (
        <div className="card-section">
          <StepLabel n="2" text="Select Games" />
          <p style={{ fontSize: 12, color: '#607d8b', marginBottom: 12 }}>
            Only games that meet <strong>all</strong> of these conditions can be selected:
            the global config exists (<span className="badge bg-success" style={{ fontSize: 10 }}>In DB</span>),
            the global config is active,
            and the game is not already active for this casino.
            Games marked <span className="badge bg-warning text-dark" style={{ fontSize: 10 }}>Not in DB</span> need the <strong>Import Game Configs</strong> tab first.
          </p>
          {loadingGames
            ? <div className="text-center p-3"><div className="spinner-border text-secondary" role="status" /></div>
            : <div style={{ overflowX: 'auto' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    {selectedGames.length} / {selectableGames.length} selectable selected
                    {games.length !== selectableGames.length && (
                      <span style={{ color: '#6b7280', marginLeft: 8 }}>
                        ({games.length - selectableGames.length} not selectable)
                      </span>
                    )}
                  </span>
                  <button className="btn btn-outline-secondary btn-sm" onClick={toggleAll} disabled={selectableGames.length === 0}>
                    {allSelected ? 'Deselect All' : `Select All (${selectableGames.length})`}
                  </button>
                </div>
                <table className="table table-hover table-sm align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={selectableGames.length === 0} />
                      </th>
                      <th>Game Name</th><th>Provider Game ID</th><th>BW Game Code</th><th>Category</th>
                      <th>In DB</th><th>Config Active</th><th>Added to Casino</th><th>Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrichedGames.map(g => {
                      const code = getGameCode(g);
                      const sel = selectedGames.includes(code);
                      const ce = g._casinoEntry;
                      const globalConfigReady = g.is_in_db && g.is_active_game_config === 1;
                      const alreadyActive     = ce?.is_active === 1 || ce?.is_active === true;
                      const alreadyAdded      = ce?.is_added === true || ce?.is_added === 1;
                      const canSelect = globalConfigReady && !(alreadyAdded && alreadyActive);
                      const disabledReason = !g.is_in_db
                        ? 'import game config first'
                        : g.is_active_game_config !== 1
                          ? 'game config not active yet'
                          : (alreadyAdded && alreadyActive)
                            ? 'already added & active for this casino'
                            : null;
                      return (
                        <tr key={code}
                          onClick={() => toggleGame(code, canSelect)}
                          style={{ cursor: canSelect ? 'pointer' : 'not-allowed', background: sel ? '#fdf2f8' : canSelect ? undefined : '#fafafa', opacity: canSelect ? 1 : 0.55 }}
                        >
                          <td onClick={e => e.stopPropagation()}>
                            <input type="checkbox" checked={sel} onChange={() => toggleGame(code, canSelect)} disabled={!canSelect} />
                          </td>
                          <td>
                            <strong style={{ fontSize: 13 }}>{g.name}</strong>
                            {disabledReason && <span style={{ fontSize: 10, color: '#6b7280', marginLeft: 6 }}>{disabledReason}</span>}
                          </td>
                          <td><code style={{ fontSize: 11 }}>{code}</code></td>
                          <td>
                            {ce?.internal_game_id
                              ? <code style={{ fontSize: 11 }}>{stripCasinoPrefix(ce.internal_game_id)}</code>
                              : <span className="text-muted">—</span>
                            }
                          </td>
                          <td><span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>{g.category ?? '—'}</span></td>
                          <td><span className={`badge ${g.is_in_db ? 'bg-success' : 'bg-warning text-dark'}`}>{g.is_in_db ? 'In DB' : 'Not in DB'}</span></td>
                          <td>{activeConfigBadge(g.is_active_game_config)}</td>
                          <td>
                            {ce
                              ? <span className={`badge ${ce.is_added ? 'bg-success' : 'bg-secondary'}`}>{ce.is_added ? 'Added' : 'Not Added'}</span>
                              : <span className="text-muted">—</span>
                            }
                          </td>
                          <td>
                            {ce?.is_active !== null && ce?.is_active !== undefined
                              ? <span className={`badge ${ce.is_active ? 'bg-success' : 'bg-danger'}`}>{ce.is_active ? 'Active' : 'Inactive'}</span>
                              : <span className="text-muted">—</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
          }
        </div>
      )}

      {selectedGames.length > 0 && (
        <div className="card-section">
          <StepLabel n="3" text="Confirm & Import" />
          <div className="import-panel">
            <h6>Operator Game Config Import</h6>
            <table className="table table-sm mb-2" style={{ fontSize: 12 }}>
              <tbody>
                <tr><td className="text-muted" style={{ width: 130 }}>Aggregator</td><td><strong>{aggregators.find(a => String(a.id) === String(aggId))?.name ?? aggId}</strong></td></tr>
                <tr><td className="text-muted">Provider</td><td><strong>{providers.find(p => String(p.id) === String(providerId))?.name ?? providerId}</strong></td></tr>
                <tr><td className="text-muted">Casino</td><td><strong>{casinos.find(c => String(c.id) === String(casinoId))?.company_name ?? casinoId}</strong></td></tr>
                <tr><td className="text-muted">Games selected</td><td><strong>{selectedGames.length}</strong></td></tr>
                <tr><td className="text-muted">Endpoint</td><td><code style={{ fontSize: 11 }}>/import-operator-game-configs</code></td></tr>
              </tbody>
            </table>
            <button className="btn btn-flexpink btn-sm" onClick={openImportConfirm} disabled={importing || !casinoId}>
              {`Import ${selectedGames.length} Operator Config${selectedGames.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   TAB 4 — Providers
   ══════════════════════════════════════════ */
function ProvidersTab({ aggregators, aggId, onAggChange, isGisAdmin, setLoggedIn }) {
  const [providers, setProviders] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [providerSearch, setProviderSearch] = useState('');

  const loadProviders = async (agg) => {
    const id = agg ?? aggId;
    if (!id) return;
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setLoading(true); setProviders([]); setSelectedNames([]);
    try {
      const result = await getProviders(token, id);
      setProviders(Array.isArray(result) ? result : []);
    } catch { toast.error('Failed to load providers'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (aggId) loadProviders(); }, [aggId]);

  const handleAggChange = (v) => { onAggChange(v); setProviders([]); setSelectedNames([]); };

  const isSelectable = (p) => !p.is_in_db || !p.is_active;

  const toggleName = (name) =>
    setSelectedNames(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);

  const selectableProviders = providers.filter(isSelectable);
  const allSelected = selectableProviders.length > 0 && selectedNames.length === selectableProviders.length;
  const toggleAll = () => setSelectedNames(allSelected ? [] : selectableProviders.map(p => p.name));

  const filteredProviders = providerSearch.trim()
    ? providers.filter(p => p.name.toLowerCase().includes(providerSearch.toLowerCase()))
    : providers;

  const handleImportMissing = async () => {
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setImporting(true);
    try {
      const namesToImport = selectedNames.length > 0 ? selectedNames : null;
      const result = await importMissingProviders(token, aggId, namesToImport);
      if (result) {
        const created = result.created?.length ?? 0;
        const activated = result.activated?.length ?? 0;
        setImportResult({ created, activated });
        setSelectedNames([]);
        await loadProviders();
      }
    } catch { toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  const statusBadge = (p) => {
    if (p.is_in_db && p.is_active) return <span className="badge bg-success">Active</span>;
    if (p.is_in_db && !p.is_active) return <span className="badge bg-warning text-dark">In DB, Inactive</span>;
    return <span className="badge bg-secondary">Not in DB</span>;
  };

  const inDbBadge = (val) => (
    <span className={`badge ${val ? 'bg-success' : 'bg-warning text-dark'}`}>{val ? 'Yes' : 'No'}</span>
  );

  const inAggBadge = (val) => (
    <span className={`badge ${val ? 'bg-success' : 'bg-secondary'}`}>{val ? 'Yes' : 'No'}</span>
  );

  const missingCount = providers.filter(p => !p.is_in_db).length;
  const inactiveCount = providers.filter(p => p.is_in_db && !p.is_active).length;

  return (
    <div>
      {importResult && (
        <div className="alert alert-success d-flex justify-content-between align-items-center mb-3" role="alert">
          <span>
            Import complete — <strong>{importResult.created}</strong> created,{' '}
            <strong>{importResult.activated}</strong> activated.
          </span>
          <button type="button" className="btn-close" onClick={() => setImportResult(null)} />
        </div>
      )}

      <div className="card-section">
        <StepLabel n="1" text="Select Aggregator" />
        <div className="filter-row">
          <SelectInput value={aggId} onChange={handleAggChange}>
            <option value="">— Select aggregator —</option>
            {aggregators.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </SelectInput>
        </div>
      </div>

      {(providers.length > 0 || loading) && (
        <div className="card-section">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h6 className="mb-0" style={{ color: '#2e2e31' }}>
                {providers.length} providers
              </h6>
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ width: 200, fontSize: 12 }}
                placeholder="Search providers…"
                value={providerSearch}
                onChange={e => setProviderSearch(e.target.value)}
              />
              {(missingCount > 0 || inactiveCount > 0) && (
                <p style={{ fontSize: 12, color: '#d97706', margin: 0 }}>
                  {missingCount > 0 && <span>{missingCount} not in DB</span>}
                  {missingCount > 0 && inactiveCount > 0 && <span> · </span>}
                  {inactiveCount > 0 && <span>{inactiveCount} inactive</span>}
                </p>
              )}
            </div>

            {isGisAdmin && (
              <div className="d-flex align-items-center gap-2">
                {selectableProviders.length > 0 && (
                  <button className="btn btn-outline-secondary btn-sm" onClick={toggleAll}>
                    {allSelected ? 'Deselect All' : `Select All Missing/Inactive (${selectableProviders.length})`}
                  </button>
                )}
                <button
                  className="btn btn-flexpink btn-sm"
                  onClick={handleImportMissing}
                  disabled={importing || !aggId}
                >
                  {importing
                    ? <><span className="spinner-border spinner-border-sm me-1" />Importing…</>
                    : selectedNames.length > 0
                      ? `Import Selected (${selectedNames.length})`
                      : 'Import All Missing'
                  }
                </button>
              </div>
            )}
          </div>

          {loading
            ? <div className="text-center p-3"><div className="spinner-border text-secondary" role="status" /></div>
            : <div style={{ overflowX: 'auto' }}>
                <table className="table table-hover table-sm align-middle">
                  <thead>
                    <tr>
                      {isGisAdmin && <th style={{ width: 36 }}></th>}
                      <th>ID</th>
                      <th>Name</th>
                      <th>Redirect Path</th>
                      <th>Aggregator ID</th>
                      <th>In DB</th>
                      <th>In Aggregator</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProviders.map((p, i) => {
                      const selectable = isSelectable(p);
                      const sel = selectedNames.includes(p.name);
                      return (
                        <tr
                          key={p.id ?? `missing-${i}`}
                          onClick={() => isGisAdmin && selectable && toggleName(p.name)}
                          style={{
                            cursor: isGisAdmin && selectable ? 'pointer' : 'default',
                            background: sel ? '#fdf2f8' : !selectable ? '#f9fafb' : undefined,
                            opacity: !selectable ? 0.7 : 1,
                          }}
                        >
                          {isGisAdmin && (
                            <td onClick={e => e.stopPropagation()}>
                              {selectable && (
                                <input
                                  type="checkbox"
                                  checked={sel}
                                  onChange={() => toggleName(p.name)}
                                />
                              )}
                            </td>
                          )}
                          <td>
                            {p.id
                              ? <span className="badge bg-secondary">{p.id}</span>
                              : <span className="text-muted">—</span>
                            }
                          </td>
                          <td><strong style={{ fontSize: 13 }}>{p.name}</strong></td>
                          <td>
                            {p.redirect_path
                              ? <code style={{ fontSize: 11 }}>{p.redirect_path}</code>
                              : <span className="text-muted">—</span>
                            }
                          </td>
                          <td><span className="badge bg-light text-dark border">{p.agregator_id}</span></td>
                          <td>{inDbBadge(p.is_in_db)}</td>
                          <td>{inAggBadge(p.is_in_aggregator)}</td>
                          <td>{statusBadge(p)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
          }
        </div>
      )}

      {!aggId && (
        <div className="card-section">
          <div className="empty-state">
            <div className="empty-state-icon">◈</div>
            <p>Select an aggregator to view its providers</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   ROOT GamesPage
   ══════════════════════════════════════════ */
const TABS = [
  { key: 'browse',    label: 'Browse Games',            role: null },
  { key: 'configs',   label: 'Import Game Configs',     role: 'gis-admin' },
  { key: 'operator',  label: 'Import Operator Configs', role: 'gis-admin' },
];

function GamesPage({ setLoggedIn }) {
  const [activeTab, setActiveTab] = useState('browse');

  const [aggregators, setAggregators]     = useState([]);
  const [casinos, setCasinos]             = useState([]);
  const [aggId, setAggId]                 = useState('');
  const [providers, setProviders]         = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingMeta, setLoadingMeta]     = useState(false);

  const roles = getWithExpiry('roles') ?? [];
  const isGisAdmin = Array.isArray(roles)
    ? roles.includes('gis-admin')
    : String(roles).includes('gis-admin');

  const visibleTabs = TABS.filter(t => !t.role || isGisAdmin);

  useEffect(() => {
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setLoadingMeta(true);
    Promise.all([getAggregators(token), getCasinos(token)])
      .then(([aggs, cas]) => {
        if (aggs?.length) setAggregators(aggs);
        if (cas?.length) setCasinos(cas.filter(c => c.is_active));
      })
      .catch(() => toast.error('Failed to load metadata'))
      .finally(() => setLoadingMeta(false));
  }, [setLoggedIn]);

  useEffect(() => {
    if (!aggId) { setProviders([]); return; }
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setLoadingProviders(true);
    setProviders([]);
    getDbProviders(token, aggId)
      .then(p => { if (p) setProviders(p); })
      .catch(() => toast.error('Failed to load providers'))
      .finally(() => setLoadingProviders(false));
  }, [aggId, setLoggedIn]);

  const handleAggChange = (newAggId) => setAggId(newAggId);

  return (
    <div>
      <div className="page-header">
        <div>
          <h4 className="page-title">Import Games</h4>
          <p className="page-subtitle">Browse games and import configurations to the wallet</p>
        </div>
        {(loadingMeta || loadingProviders) && (
          <span className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
            <span className="spinner-border spinner-border-sm" />
            {loadingMeta ? 'Loading…' : 'Loading providers…'}
          </span>
        )}
      </div>

      <ul className="nav nav-tabs mb-3">
        {visibleTabs.map(t => (
          <li className="nav-item" key={t.key}>
            <button
              className={`nav-link ${activeTab === t.key ? 'active' : ''}`}
              style={{ border: 'none', background: 'none', fontFamily: 'Lato, sans-serif' }}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      <div style={{ display: activeTab === 'browse' ? 'block' : 'none' }}>
        <BrowseTab
          aggregators={aggregators} providers={providers} loadingProviders={loadingProviders}
          aggId={aggId} onAggChange={handleAggChange}
          casinos={casinos} setLoggedIn={setLoggedIn}
        />
      </div>
      {isGisAdmin && (
        <div style={{ display: activeTab === 'configs' ? 'block' : 'none' }}>
          <ImportGameConfigsTab
            aggregators={aggregators} providers={providers} loadingProviders={loadingProviders}
            aggId={aggId} onAggChange={handleAggChange}
            setLoggedIn={setLoggedIn}
          />
        </div>
      )}
      {isGisAdmin && (
        <div style={{ display: activeTab === 'operator' ? 'block' : 'none' }}>
          <ImportOperatorTab
            aggregators={aggregators} providers={providers} loadingProviders={loadingProviders}
            aggId={aggId} onAggChange={handleAggChange}
            casinos={casinos} setLoggedIn={setLoggedIn}
          />
        </div>
      )}
      <div style={{ display: activeTab === 'providers' ? 'block' : 'none' }}>
        <ProvidersTab
          aggregators={aggregators}
          aggId={aggId} onAggChange={handleAggChange}
          isGisAdmin={isGisAdmin}
          setLoggedIn={setLoggedIn}
        />
      </div>
    </div>
  );
}

export default GamesPage;
